"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

function labelFor(el: HTMLElement) {
  return (
    el.getAttribute("data-analytics-label") ||
    el.getAttribute("aria-label") ||
    el.textContent ||
    ""
  )
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 90);
}

function sectionFor(el: HTMLElement) {
  return (
    el.closest("section")?.id ||
    el.closest("[data-section]")?.getAttribute("data-section") ||
    ""
  );
}

export default function AnalyticsTracker() {
  useEffect(() => {
    const startTracking = () => {
      trackEvent("page_view", {
        width: window.innerWidth,
        height: window.innerHeight,
        language: navigator.language,
      });

      const startedForms = new WeakSet<HTMLFormElement>();
      const scrollDepths = new Set<number>();
      let engaged = false;
      // Rapid double/triple clicks on the same control fired one cta_clicked per
      // click (observed: 5 identical "Services" events inside one second), which
      // inflates the funnel's cta counts. Collapse repeats of the SAME element
      // within a short window into one event. Keyed by the element itself, so
      // interleaved rage clicks are still suppressed and two different controls
      // that share a label and destination are never merged.
      const recentCta = new WeakMap<HTMLElement, number>();

      function onClick(event: MouseEvent) {
        const target = event.target instanceof Element ? event.target : null;
        const el = target?.closest("a,button") as HTMLElement | null;
        if (!el) return;

        const href = el instanceof HTMLAnchorElement ? el.href : "";
        const label = labelFor(el);
        const section = sectionFor(el);
        const now = performance.now();
        const prev = recentCta.get(el);
        recentCta.set(el, now);
        if (prev !== undefined && now - prev < 1000) return;

        if (href.includes("wa.me") || /whatsapp/i.test(label)) {
          trackEvent("whatsapp_opened", { label, section });
          return;
        }

        if (el.hasAttribute("data-cal-link") || /calendar/i.test(label)) {
          trackEvent("calendar_clicked", { label, section });
          return;
        }

        if (href && new URL(href, location.href).origin !== location.origin) {
          trackEvent("outbound_link_clicked", {
            label,
            section,
            host: new URL(href).hostname,
          });
          return;
        }

        if (label || section) {
          trackEvent("cta_clicked", {
            label,
            section,
            href: href ? new URL(href).pathname : "",
          });
        }
      }

      function onFocusIn(event: FocusEvent) {
        const target = event.target instanceof Element ? event.target : null;
        const form = target?.closest("form") as HTMLFormElement | null;
        if (!form || startedForms.has(form)) return;
        startedForms.add(form);
        trackEvent("form_started", {
          form: form.className || form.id || "form",
          section: sectionFor(form),
        });
      }

      function onSubmit(event: SubmitEvent) {
        const form =
          event.target instanceof HTMLFormElement ? event.target : null;
        if (!form) return;
        trackEvent("form_submitted", {
          form: form.className || form.id || "form",
          section: sectionFor(form),
        });
      }

      function onScroll() {
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        const pct = Math.round((window.scrollY / max) * 100);
        for (const depth of [25, 50, 75, 90]) {
          if (pct >= depth && !scrollDepths.has(depth)) {
            scrollDepths.add(depth);
            trackEvent("scroll_depth_reached", { depth });
          }
        }
      }

      const engageTimer = window.setTimeout(() => {
        engaged = true;
        trackEvent("engaged_session", { seconds: 15 });
      }, 15000);

      // Full-activity capture: pointer movement trail, every click/tap with
      // coordinates, and scroll position samples, batched into one compact
      // event every 10s. Element contents and form values are never recorded.
      const moves: string[] = [];
      const taps: string[] = [];
      const scrollTrail: string[] = [];
      let lastMove = 0;
      let lastScrollSample = 0;
      const t0 = performance.now();
      const rel = () => Math.round(performance.now() - t0);

      function onMove(event: MouseEvent | TouchEvent) {
        const now = performance.now();
        if (now - lastMove < 200) return;
        lastMove = now;
        const p = "touches" in event ? event.touches[0] : event;
        if (!p || moves.length >= 300) return;
        moves.push(
          `${Math.round(p.clientX)},${Math.round(p.clientY)},${rel()}`
        );
      }

      function onAnyClick(event: MouseEvent) {
        if (taps.length >= 50) return;
        const el = event.target instanceof HTMLElement ? event.target : null;
        const tag = el
          ? el.tagName.toLowerCase() + (el.id ? "#" + el.id : "")
          : "";
        taps.push(
          `${Math.round(event.clientX)},${Math.round(event.clientY)},${rel()},${tag}`
        );
      }

      function onScrollSample() {
        const now = performance.now();
        if (now - lastScrollSample < 500 || scrollTrail.length >= 150) {
          return;
        }
        lastScrollSample = now;
        scrollTrail.push(`${Math.round(window.scrollY)},${rel()}`);
      }

      function flushActivity(final = false) {
        if (!moves.length && !taps.length && !scrollTrail.length && !final) {
          return;
        }
        trackEvent("activity_batch", {
          trail: moves.join(";").slice(0, 3000),
          taps: taps.join(";").slice(0, 1200),
          scrolls: scrollTrail.join(";").slice(0, 1200),
          ms: rel(),
          vw: window.innerWidth,
          vh: window.innerHeight,
          final,
        });
        moves.length = 0;
        taps.length = 0;
        scrollTrail.length = 0;
      }

      const flushTimer = window.setInterval(() => flushActivity(), 10000);

      function onPageHide() {
        flushActivity(true);
        if (!engaged && document.visibilityState === "hidden") {
          trackEvent("engaged_session", {
            seconds: Math.round(performance.now() / 1000),
          });
        }
      }

      window.addEventListener("click", onClick, { capture: true });
      window.addEventListener("focusin", onFocusIn);
      window.addEventListener("submit", onSubmit, { capture: true });
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("mousemove", onMove, { passive: true });
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("click", onAnyClick, { capture: true });
      window.addEventListener("scroll", onScrollSample, { passive: true });
      window.addEventListener("pagehide", onPageHide);

      return () => {
        window.clearTimeout(engageTimer);
        window.clearInterval(flushTimer);
        window.removeEventListener("click", onClick, { capture: true });
        window.removeEventListener("focusin", onFocusIn);
        window.removeEventListener("submit", onSubmit, { capture: true });
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("click", onAnyClick, { capture: true });
        window.removeEventListener("scroll", onScrollSample);
        window.removeEventListener("pagehide", onPageHide);
      };
    };

    const cleanup = startTracking();

    return () => {
      cleanup();
    };
  }, []);

  return null;
}